import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ClipboardList,
  Edit3,
  History,
  LoaderCircle,
  PackagePlus,
  Plus,
  RefreshCw,
  Save,
  SlidersHorizontal,
  X,
} from "lucide-react";

import AdminLayout from "../components/AdminLayout";
import EmptyState from "../components/EmptyState";

import {
  adjustInventoryItem,
  createInventoryCategory,
  createInventoryItem,
  fetchInventoryCategories,
  fetchInventoryItems,
  fetchInventoryItemTransactions,
  fetchInventorySummary,
  updateInventoryItem,
} from "../inventory/inventoryApi";

const BLANK_ITEM = {
  category: "",
  name: "",
  description: "",
  sku: "",
  unit: "each",
  current_quantity: "0.000",
  low_stock_threshold: "0.000",
  target_quantity: "0.000",
  cost_per_unit: "",
  vendor_name: "",
  vendor_contact: "",
  storage_location: "",
  expiration_date: "",
  is_active: true,
  track_inventory: true,
  notes: "",
};

const UNITS = [
  ["each", "Each"],
  ["dozen", "Dozen"],
  ["ounce", "Ounces"],
  ["pound", "Pounds"],
  ["gram", "Grams"],
  ["kilogram", "Kilograms"],
  ["cup", "Cups"],
  ["pint", "Pints"],
  ["quart", "Quarts"],
  ["gallon", "Gallons"],
  ["box", "Boxes"],
  ["package", "Packages"],
  ["case", "Cases"],
  ["bag", "Bags"],
  ["bottle", "Bottles"],
  ["jar", "Jars"],
  ["roll", "Rolls"],
  ["sheet", "Sheets"],
];

const ADJUSTMENT_TYPES = [
  ["restock", "Restock"],
  ["usage", "Usage"],
  ["adjustment", "Adjustment"],
  ["waste", "Waste"],
  ["expired", "Expired"],
  ["return", "Return"],
  ["correction", "Set exact count"],
];

function formatQuantity(value) {
  const number = Number(value || 0);
  return Number.isInteger(number) ? String(number) : number.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") {
    return "$0.00";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(value));
}

function statusLabel(status) {
  if (status === "out_of_stock") {
    return "Out of stock";
  }

  if (status === "low_stock") {
    return "Low stock";
  }

  return "In stock";
}

function SummaryCard({ label, value, detail }) {
  return (
    <article className="inventory-summary-card">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function ItemForm({ categories, item, isSaving, onCancel, onSubmit }) {
  const [form, setForm] = useState(() => ({
    ...BLANK_ITEM,
    ...(item || {}),
    category: item?.category ? String(item.category) : "",
    cost_per_unit: item?.cost_per_unit ?? "",
    expiration_date: item?.expiration_date || "",
  }));
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [categoryError, setCategoryError] = useState("");

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleCreateCategory() {
    const name = newCategoryName.trim();

    if (!name) {
      return;
    }

    setIsCreatingCategory(true);
    setCategoryError("");

    try {
      const category = await createInventoryCategory({ name });
      setForm((current) => ({ ...current, category: String(category.id) }));
      setNewCategoryName("");
    } catch (error) {
      setCategoryError(error.message || "Category could not be created.");
    } finally {
      setIsCreatingCategory(false);
    }
  }

  function submit(event) {
    event.preventDefault();

    const payload = {
      ...form,
      category: Number(form.category),
      cost_per_unit: form.cost_per_unit === "" ? null : form.cost_per_unit,
      expiration_date: form.expiration_date || null,
    };

    if (item?.id) {
      delete payload.current_quantity;
    }

    onSubmit(payload);
  }

  return (
    <div className="admin-modal-backdrop">
      <section className="inventory-modal" role="dialog" aria-modal="true">
        <div className="inventory-modal__header">
          <div>
            <span className="admin-eyebrow">Inventory</span>
            <h2>{item?.id ? "Edit item" : "Add item"}</h2>
          </div>
          <button type="button" className="inventory-icon-button" onClick={onCancel} disabled={isSaving} aria-label="Close form">
            <X size={20} />
          </button>
        </div>

        <form className="inventory-form" onSubmit={submit}>
          <label>
            Category
            <select required value={form.category} onChange={(event) => updateField("category", event.target.value)}>
              <option value="">Select category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </label>

          <label>
            New category
            <span className="inventory-inline-input">
              <input value={newCategoryName} onChange={(event) => setNewCategoryName(event.target.value)} placeholder="Packaging, dairy, produce..." />
              <button type="button" className="btn btn--secondary" onClick={handleCreateCategory} disabled={isCreatingCategory || !newCategoryName.trim()}>
                {isCreatingCategory ? <LoaderCircle size={16} className="admin-spin" /> : <Plus size={16} />}
                Add
              </button>
            </span>
            {categoryError && <small className="inventory-field-error">{categoryError}</small>}
          </label>

          <label>
            Item name
            <input required value={form.name} onChange={(event) => updateField("name", event.target.value)} />
          </label>

          <label>
            SKU
            <input value={form.sku} onChange={(event) => updateField("sku", event.target.value)} />
          </label>

          <label>
            Unit
            <select value={form.unit} onChange={(event) => updateField("unit", event.target.value)}>
              {UNITS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>

          {!item?.id && (
            <label>
              Current quantity
              <input type="number" min="0" step="0.001" required value={form.current_quantity} onChange={(event) => updateField("current_quantity", event.target.value)} />
            </label>
          )}

          <label>
            Low-stock threshold
            <input type="number" min="0" step="0.001" required value={form.low_stock_threshold} onChange={(event) => updateField("low_stock_threshold", event.target.value)} />
          </label>

          <label>
            Target quantity
            <input type="number" min="0" step="0.001" required value={form.target_quantity} onChange={(event) => updateField("target_quantity", event.target.value)} />
          </label>

          <label>
            Cost per unit
            <input type="number" min="0" step="0.01" value={form.cost_per_unit ?? ""} onChange={(event) => updateField("cost_per_unit", event.target.value)} />
          </label>

          <label>
            Vendor
            <input value={form.vendor_name} onChange={(event) => updateField("vendor_name", event.target.value)} />
          </label>

          <label>
            Vendor contact
            <input value={form.vendor_contact} onChange={(event) => updateField("vendor_contact", event.target.value)} />
          </label>

          <label>
            Storage location
            <input value={form.storage_location} onChange={(event) => updateField("storage_location", event.target.value)} />
          </label>

          <label>
            Expiration date
            <input type="date" value={form.expiration_date} onChange={(event) => updateField("expiration_date", event.target.value)} />
          </label>

          <label className="inventory-form__wide">
            Description
            <textarea value={form.description} onChange={(event) => updateField("description", event.target.value)} rows={3} />
          </label>

          <label className="inventory-form__wide">
            Notes
            <textarea value={form.notes} onChange={(event) => updateField("notes", event.target.value)} rows={3} />
          </label>

          <label className="inventory-check">
            <input type="checkbox" checked={form.is_active} onChange={(event) => updateField("is_active", event.target.checked)} />
            Active item
          </label>

          <label className="inventory-check">
            <input type="checkbox" checked={form.track_inventory} onChange={(event) => updateField("track_inventory", event.target.checked)} />
            Track inventory
          </label>

          <footer className="inventory-modal__footer">
            <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={isSaving}>Cancel</button>
            <button type="submit" className="btn" disabled={isSaving}>
              {isSaving ? <LoaderCircle size={17} className="admin-spin" /> : <Save size={17} />}
              {isSaving ? "Saving..." : "Save item"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}

function AdjustmentModal({ item, isSaving, onCancel, onSubmit }) {
  const [form, setForm] = useState({
    transaction_type: "restock",
    quantity: "",
    new_quantity: "",
    reason: "",
    reference: "",
    notes: "",
  });
  const isCorrection = form.transaction_type === "correction";

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function submit(event) {
    event.preventDefault();
    onSubmit({
      ...form,
      quantity: isCorrection ? undefined : form.quantity,
      new_quantity: isCorrection ? form.new_quantity : undefined,
    });
  }

  return (
    <div className="admin-modal-backdrop">
      <section className="inventory-modal inventory-modal--small" role="dialog" aria-modal="true">
        <div className="inventory-modal__header">
          <div>
            <span className="admin-eyebrow">Stock adjustment</span>
            <h2>{item.name}</h2>
            <p>{formatQuantity(item.current_quantity)} {item.unit_label}</p>
          </div>
          <button type="button" className="inventory-icon-button" onClick={onCancel} disabled={isSaving} aria-label="Close adjustment">
            <X size={20} />
          </button>
        </div>

        <form className="inventory-form inventory-form--single" onSubmit={submit}>
          <label>
            Adjustment type
            <select value={form.transaction_type} onChange={(event) => update("transaction_type", event.target.value)}>
              {ADJUSTMENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label>
            {isCorrection ? "New exact quantity" : "Quantity"}
            <input required type="number" min="0" step="0.001" value={isCorrection ? form.new_quantity : form.quantity} onChange={(event) => update(isCorrection ? "new_quantity" : "quantity", event.target.value)} />
          </label>
          <label>
            Reason
            <input required value={form.reason} onChange={(event) => update("reason", event.target.value)} />
          </label>
          <label>
            Reference
            <input value={form.reference} onChange={(event) => update("reference", event.target.value)} />
          </label>
          <label>
            Notes
            <textarea rows={3} value={form.notes} onChange={(event) => update("notes", event.target.value)} />
          </label>
          <footer className="inventory-modal__footer">
            <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={isSaving}>Cancel</button>
            <button type="submit" className="btn" disabled={isSaving}>
              {isSaving ? <LoaderCircle size={17} className="admin-spin" /> : <PackagePlus size={17} />}
              {isSaving ? "Saving..." : "Save adjustment"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}

function HistoryDrawer({ item, transactions, isLoading, onClose }) {
  return (
    <div className="admin-modal-backdrop">
      <aside className="inventory-drawer" role="dialog" aria-modal="true">
        <div className="inventory-modal__header">
          <div>
            <span className="admin-eyebrow">History</span>
            <h2>{item.name}</h2>
          </div>
          <button type="button" className="inventory-icon-button" onClick={onClose} aria-label="Close history">
            <X size={20} />
          </button>
        </div>

        {isLoading ? (
          <div className="inventory-loading"><LoaderCircle className="admin-spin" size={22} /> Loading transactions...</div>
        ) : transactions.length === 0 ? (
          <EmptyState icon={History} title="No transactions yet" description="Adjustments, usage, and restocks will appear here." />
        ) : (
          <div className="inventory-history-list">
            {transactions.map((transaction) => (
              <article key={transaction.id} className="inventory-history-row">
                <div>
                  <strong>{transaction.transaction_type_label}</strong>
                  <span>{transaction.reason}</span>
                  <small>{new Date(transaction.created_at).toLocaleString()}</small>
                </div>
                <div className={Number(transaction.quantity_change) >= 0 ? "inventory-positive" : "inventory-negative"}>
                  {Number(transaction.quantity_change) >= 0 ? "+" : ""}{formatQuantity(transaction.quantity_change)}
                  <small>{formatQuantity(transaction.quantity_before)} to {formatQuantity(transaction.quantity_after)}</small>
                </div>
              </article>
            ))}
          </div>
        )}
      </aside>
    </div>
  );
}

function InventoryItemCard({ item, onAdjust, onEdit, onHistory }) {
  return (
    <article className="inventory-item-card">
      <div className="inventory-item-card__main">
        <span className={`inventory-status inventory-status--${item.stock_status}`}>{statusLabel(item.stock_status)}</span>
        <h3>{item.name}</h3>
        <p>{item.category_name} {item.sku ? `- ${item.sku}` : ""}</p>
      </div>
      <div className="inventory-quantity">
        <strong>{formatQuantity(item.current_quantity)}</strong>
        <span>{item.unit_label}</span>
      </div>
      <dl>
        <div><dt>Low</dt><dd>{formatQuantity(item.low_stock_threshold)}</dd></div>
        <div><dt>Target</dt><dd>{formatQuantity(item.target_quantity)}</dd></div>
        <div><dt>Value</dt><dd>{formatMoney(item.estimated_inventory_value)}</dd></div>
        <div><dt>Location</dt><dd>{item.storage_location || "Not set"}</dd></div>
      </dl>
      <div className="inventory-progress" aria-label={`${item.stock_percentage}% stocked`}>
        <span style={{ width: `${Math.min(item.stock_percentage || 0, 100)}%` }} />
      </div>
      <div className="inventory-card-actions">
        <button type="button" className="btn btn--secondary" onClick={() => onHistory(item)}><History size={16} /> History</button>
        <button type="button" className="btn btn--secondary" onClick={() => onEdit(item)}><Edit3 size={16} /> Edit</button>
        <button type="button" className="btn" onClick={() => onAdjust(item)}><SlidersHorizontal size={16} /> Adjust</button>
      </div>
    </article>
  );
}

export default function AdminInventoryPage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [adjustingItem, setAdjustingItem] = useState(null);
  const [historyItem, setHistoryItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const loadData = useCallback(async (signal) => {
    setIsLoading(true);
    setError("");
    try {
      const [categoryData, itemData, summaryData] = await Promise.all([
        fetchInventoryCategories({ active: "", signal }),
        fetchInventoryItems({ search: debouncedSearch, category, status, ordering: "category__display_order,name", signal }),
        fetchInventorySummary({ signal }),
      ]);
      setCategories(categoryData);
      setItems(itemData);
      setSummary(summaryData);
    } catch (requestError) {
      if (requestError.name !== "AbortError") {
        setError(requestError.message || "Inventory could not be loaded.");
      }
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  }, [category, debouncedSearch, status]);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [loadData]);

  const filteredLabel = useMemo(() => {
    if (isLoading) {
      return "Loading inventory...";
    }
    return `${items.length} ${items.length === 1 ? "item" : "items"}`;
  }, [isLoading, items.length]);

  function openCreate() {
    setEditingItem(null);
    setIsFormOpen(true);
  }

  function openEdit(item) {
    setEditingItem(item);
    setIsFormOpen(true);
  }

  async function saveItem(payload) {
    setIsSaving(true);
    setError("");
    try {
      const saved = editingItem?.id ? await updateInventoryItem(editingItem.id, payload) : await createInventoryItem(payload);
      setSuccess(`${saved.name} was saved.`);
      setIsFormOpen(false);
      setEditingItem(null);
      await loadData();
      window.setTimeout(() => setSuccess(""), 3500);
    } catch (requestError) {
      setError(requestError.message || "Inventory item could not be saved.");
    } finally {
      setIsSaving(false);
    }
  }

  async function saveAdjustment(payload) {
    setIsSaving(true);
    setError("");
    try {
      const result = await adjustInventoryItem(adjustingItem.id, payload);
      setSuccess(`${result.item.name} stock was updated.`);
      setAdjustingItem(null);
      await loadData();
      window.setTimeout(() => setSuccess(""), 3500);
    } catch (requestError) {
      setError(requestError.message || "Stock could not be adjusted.");
    } finally {
      setIsSaving(false);
    }
  }

  async function openHistory(item) {
    setHistoryItem(item);
    setIsHistoryLoading(true);
    try {
      setHistory(await fetchInventoryItemTransactions(item.id));
    } catch (requestError) {
      setError(requestError.message || "Inventory history could not be loaded.");
    } finally {
      setIsHistoryLoading(false);
    }
  }

  return (
    <AdminLayout>
      <section className="admin-page inventory-page">
        <header className="admin-page-heading">
          <div>
            <span className="admin-eyebrow">Inventory</span>
            <h1>Inventory</h1>
            <p>Track ingredients, packaging, stock levels, vendors, and usage history.</p>
          </div>
          <div className="inventory-heading-actions">
            <button type="button" className="btn btn--secondary" onClick={() => loadData()} disabled={isLoading}><RefreshCw size={17} /> Refresh</button>
            <button type="button" className="btn" onClick={openCreate}><Plus size={17} /> Add item</button>
          </div>
        </header>

        {error && <div className="admin-menu-error"><AlertCircle size={18} /><span>{error}</span></div>}
        {success && <div className="inventory-success">{success}</div>}

        <div className="inventory-summary-grid">
          <SummaryCard label="Total items" value={summary?.total_items ?? "-"} detail="Tracked inventory records" />
          <SummaryCard label="Low stock" value={summary?.low_stock_items ?? "-"} detail="At or under threshold" />
          <SummaryCard label="Out of stock" value={summary?.out_of_stock_items ?? "-"} detail="Quantity is zero" />
          <SummaryCard label="Inventory value" value={formatMoney(summary?.estimated_inventory_value)} detail="Estimated from unit cost" />
        </div>

        <section className="inventory-toolbar">
          <label>
            Search
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search item, SKU, vendor, location" />
          </label>
          <label>
            Category
            <select value={category} onChange={(event) => setCategory(event.target.value)}>
              <option value="">All categories</option>
              {categories.map((categoryItem) => <option key={categoryItem.id} value={categoryItem.id}>{categoryItem.name}</option>)}
            </select>
          </label>
          <label>
            Status
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All statuses</option>
              <option value="in_stock">In stock</option>
              <option value="low_stock">Low stock</option>
              <option value="out_of_stock">Out of stock</option>
            </select>
          </label>
          <strong>{filteredLabel}</strong>
        </section>

        {isLoading ? (
          <div className="inventory-loading"><LoaderCircle className="admin-spin" size={24} /> Loading inventory...</div>
        ) : items.length === 0 ? (
          <EmptyState icon={ClipboardList} title="No inventory items yet" description="Add ingredients, packaging, and supplies to begin tracking stock." actionLabel="Add item" onAction={openCreate} />
        ) : (
          <div className="inventory-grid">
            {items.map((item) => (
              <InventoryItemCard key={item.id} item={item} onAdjust={setAdjustingItem} onEdit={openEdit} onHistory={openHistory} />
            ))}
          </div>
        )}
      </section>

      {isFormOpen && <ItemForm categories={categories} item={editingItem} isSaving={isSaving} onCancel={() => !isSaving && setIsFormOpen(false)} onSubmit={saveItem} />}
      {adjustingItem && <AdjustmentModal item={adjustingItem} isSaving={isSaving} onCancel={() => !isSaving && setAdjustingItem(null)} onSubmit={saveAdjustment} />}
      {historyItem && <HistoryDrawer item={historyItem} transactions={history} isLoading={isHistoryLoading} onClose={() => setHistoryItem(null)} />}
    </AdminLayout>
  );
}
